<template>
  <div>
    <section>
      <h2 class="bg-beige1 pt-12 text-center text-xl font-normal md:pt-16 md:text-2xl">Details</h2>
      <div class="bg-beige1 pb-7 pt-2">
        <div class="mx-auto w-12 border-b border-gold1 md:w-14" />
      </div>
      <!-- ArticleSection -->
      <template v-for="(content, idx) in articleSectionContents" :key="`articleSection-${idx}`">
        <ArticleSection :experiences="experiences" :is-bg-beige="true" :on-drawer-link-click="onDrawerLinkClick" :section="content" :show-drawer-link="showDrawerLink" />
      </template>
      <div class="bg-beige1 pb-8 md:mx-[5%] md:pb-0 e2:m-auto e2:max-w-6xl">
        <div class="text-base md:px-[8%] lg:m-auto lg:max-w-3xl lg:p-0">
          <!-- Base contents -->
          <template v-for="(content, idx) in experienceCms.contents" :key="`${content.fieldId}-${idx}`">
            <div class="cms-detail-block px-4 pt-2 md:p-0">
              <StyledRichText v-if="content.fieldId === 'richEditor'" :html="content.richEditor || ''" />
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-if="content.fieldId === 'html'" v-html="content.html"></div>
              <!-- 体験詳細内画像 -->
              <div v-if="content.image && content.image.url">
                <OptimizedImage :alt="''" :image-url="content.image.url" :optimized-image-options="{ width: 1152 }" />
              </div>
            </div>
          </template>
          <template v-if="experienceCms.providerName">
            <div class="pb-8 font-Spectral tracking-normal md:pb-10">
              <h3 class="hidden text-xl text-gold1 md:block">{{ experienceCms.providerName }}</h3>
              <div class="mt-4 flex flex-wrap items-center px-4 md:flex-nowrap md:px-0">
                <!-- 体験詳細内プロバイダー -->
                <OptimizedImage
                  :alt="experienceCms.providerImg.url"
                  :class="['mb-3 mr-3 h-auto w-2/5 max-w-[260px] md:mb-0 md:mr-6']"
                  :image-url="experienceCms.providerImg.url"
                  :optimized-image-options="{ width: 260 }"
                />
                <h3 class="w-[55%] text-base text-gold1 md:hidden"><br />{{ experienceCms.providerName }}</h3>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <p class="text-base text-black2 md:text-justify" v-html="experienceCms.providerDesc"></p>
              </div>
            </div>
          </template>
          <template v-if="experienceCms.stories && experienceCms.stories.length > 0">
            <div class="flex flex-col gap-y-4 px-4 pb-8 md:px-0 md:pb-10">
              <StoryWideCard v-for="story in experienceCms.stories" :key="story.id" :story="story" />
            </div>
          </template>
          <div class="mx-auto w-56 md:w-96">
            <AtomsPrimaryButton data-testid="check-availability-button" :on-click="onClickCheckAvailability">Check availability</AtomsPrimaryButton>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import type { EnExperienceViewModel } from '@/models/enExperience';
import { buildArticleSectionContents, canShowDrawerLink } from '@/models/enExperience';
import { OptimizedImage } from '@/wabunka-src/common/components';
import ArticleSection from '@/wabunka-src/common/features/Featured/components/ArticleSection/ArticleSection.vue';
import StyledRichText from '@/wabunka-src/common/features/Featured/components/StyledRichText/StyledRichText.vue';
import { useExtractExperienceIds } from '@/wabunka-src/features/Experience/composables/general/useExtractExperienceIds';
import { useFetchExperiencesQuery } from '@/wabunka-src/features/Experience/composables/query/useFetchExperiencesQuery';

interface Props {
  experienceCms: EnExperienceViewModel;
  onClickCheckAvailability: () => void;
  onDrawerLinkClick: (tabKey: string, label: string) => void;
}

const props = defineProps<Props>();

const { experienceIds } = useExtractExperienceIds(toRef(props, 'experienceCms'));

const experiencesData =
  experienceIds.value?.length > 0
    ? await useFetchExperiencesQuery({
        experienceEventStartAtDates: ref(''),
        destinations: ref(''),
        themes: ref(''),
        slugs: experienceIds,
      })
    : { experiences: computed(() => []) };
const experiences = experiencesData.experiences;

const articleSectionContents = computed(() => buildArticleSectionContents(props.experienceCms.contents));

const showDrawerLink = computed(() => canShowDrawerLink(props.experienceCms.drawerTabs));
</script>

<style lang="scss">
.cms-detail-block {
  h3 {
    color: #836324;
    font-family: Spectral;
    font-size: 18px;
    font-weight: 300;
    line-height: 26px;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
  }

  p {
    color: #555;
    font-family: Spectral;
    font-size: 16px;
    font-weight: 300;
    line-height: 24px;
    letter-spacing: 0px;
    margin-bottom: 24px;
  }

  .caption {
    color: #999;
    font-size: 12px;
    line-height: 14px;
  }

  img {
    width: 100%;
    margin-bottom: 8px;
  }

  hr {
    margin: 32px 0;
  }
}

// PC
@media screen and (min-width: 768px) {
  .cms-detail-block {
    h3 {
      font-size: 22px;
      font-weight: 400;
      line-height: 34px;
    }

    p {
      font-size: 17px;
      line-height: 26px;
    }

    .caption {
      font-size: 14px;
    }

    hr {
      margin: 40px 0;
    }
  }
}
</style>
